import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DisplayMode, SelectBox } from 'cbc-shared-lib';
import {
  CodeConversionMstDto, Sgke401aResponseResponseCndtAuthorityList,
  Sgke903aResponseCompanyAreaInfo, Sgke903aResponseInitialhyojiInfo
} from 'sg-api';
import { ListSearchUtils } from '../../../../../sg-ke-shared/util/list-search-utils';

@Component({
  selector: 'app-sgke400d-condition',
  templateUrl: './sgke400d-condition.component.html',
  styleUrl: './sgke400d-condition.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgke400dConditionComponent implements OnInit, OnDestroy {

  /** モード */
  @Input() mode: string;
  /** コード変換マスタマップ */
  @Input() codeConversionMstMap: Map<string, Array<CodeConversionMstDto>> = null;
  /** 会社地区情報 */
  @Input() cmpnyAreaList: Array<Sgke903aResponseCompanyAreaInfo>;
  /** 初期表示用会社地区情報 */
  @Input() cmpnyAreaInit: Sgke903aResponseInitialhyojiInfo;
  /** 検索条件権限リスト */
  @Input() cndtAuthorityList: Array<Sgke401aResponseResponseCndtAuthorityList>;

  /** クリアボタン押下イベント */
  @Output() onClickClearEmitter = new EventEmitter();

  /** 親フォームグループ情報 */
  private parentForm: UntypedFormGroup;

  /** モード区分_登録 */
  public MODE_CREATE = DisplayMode.create;
  /** モード区分_承認 */
  public MODE_APPROVE = DisplayMode.approve;

  /** 検索条件フォームグループ */
  searchConditionForm: UntypedFormGroup;

  /** 会社リスト */
  companyList: SelectBox[] = [];
  /** 督促管理地区 */
  pressManageAreaList: SelectBox[] = [];
  /** 料金区分リスト */
  chargeDivList: SelectBox[] = []

  /**
   * コンストラクタ
   * @param {FormGroupDirective} parent
   * @param {UntypedFormBuilder} fb
   * @param {ListSearchUtils} listSearchUtils
   * @memberof Sgke400dConditionComponent
   */
  constructor(
    private parent: FormGroupDirective,
    private fb: UntypedFormBuilder,
    private listSearchUtils: ListSearchUtils,
  ) {
  }

  /**
   * 初期処理
   * @returns {void}
   * @memberof Sgke400dConditionComponent
   */
  ngOnInit(): void {
    // 親フォームグループ取得
    this.parentForm = this.parent.form;

    // セレクターに値をセットする
    this.companyList = this.getSelector('companycode', 'companycodeAname', this.cmpnyAreaList);
    // 督促管理地区リストを設定
    if (this.cmpnyAreaInit.companycode) {
      const newAreaList: Array<Sgke903aResponseCompanyAreaInfo> = this.cmpnyAreaList
        .filter(list => list.companycode == this.cmpnyAreaInit.companycode);
      this.pressManageAreaList = this.getSelector('areacode', 'areacodeAname', newAreaList);
    } else {
      this.pressManageAreaList = this.getSelector('areacode', 'areacodeAname', this.cmpnyAreaList);
    }

    // フォーム設定
    this.createForm();

    // 料金区分リスト設定
    this.setChargeDivList();

  }

  /**
   * 購読解除
   * @returns {void}
   * @memberof Sgke400dConditionComponent
   */
  ngOnDestroy(): void {
    this.parentForm.clearValidators();
    this.parentForm.updateValueAndValidity();
    this.parentForm.removeControl('cndt');
  }

  /**
   * 会社変更時処理
   * @public
   * @param {boolean} [isNotInitialDisplay=true] 
   * @returns {void}
   * @memberof Sgke400dConditionComponent
   */
  public onChangeCompany(isNotInitialDisplay: boolean = true): void {
    // 会社コード
    const companycode = this.getControl('companycode').value;
    // 督促管理地区
    const pressManageAreacode = this.getControl('pressManageAreacode') as UntypedFormControl;

    if (isNotInitialDisplay) {
      // 督促管理地区を未選択状態に設定
      pressManageAreacode?.setValue(null);
    }

    if (companycode) {
      const newAreaList: Array<Sgke903aResponseCompanyAreaInfo> = this.cmpnyAreaList
        .filter(list => list.companycode == companycode);
      this.pressManageAreaList = this.getSelector('areacode', 'areacodeAname', newAreaList);
    } else {
      this.pressManageAreaList = this.getSelector('areacode', 'areacodeAname', this.cmpnyAreaList);
    }

  }

  /**
   * UntypedFormControl取得
   * @public
   * @param {string} ctrlName
   * @return {UntypedFormControl}
   * @memberof Sgke400dConditionComponent
   */
  public getControl(ctrlName: string): UntypedFormControl {
    return this.searchConditionForm.get(ctrlName) as UntypedFormControl;
  }

  /**
   * クリア押下処理
   * @returns {void}
   * @memberof Sgke400dConditionComponent
   */
  public onClickClear(): void {
    this.searchConditionForm.reset();
    // 会社コードをクリアにする
    const companycode = this.getControl('companycode') as UntypedFormControl;
    companycode.setValue(null);
    // 督促管理地区をクリアにする
    const pressManageAreacode = this.getControl('pressManageAreacode') as UntypedFormControl;
    pressManageAreacode.setValue(null);

    this.onChangeCompany(false);
    this.onClickClearEmitter.emit();
  }

  /**
   * フォーム作成
   * @private
   * @returns {void}
   * @memberof Sgke400dConditionComponent
   */
  private createForm(): void {
    this.searchConditionForm = this.fb.group({
      // 督促管理区分
      pressManagementdivisionList: this.fb.nonNullable.control({ value: null, disabled: false }, this.mode != this.MODE_APPROVE ? Validators.required : null),
      // 会社
      companycode: this.fb.nonNullable.control({
        value: this.listSearchUtils.getListboxValue(this.cmpnyAreaInit.companycode, this.companyList),
        disabled: false
      }),
      // 督促管理地区
      pressManageAreacode: this.fb.nonNullable.control({
        value: this.listSearchUtils.getListboxValue(this.cmpnyAreaInit.areacode, this.pressManageAreaList),
        disabled: false
      }),
      // 料金区分
      chargeDivcd: this.fb.nonNullable.control({ value: null, disabled: false }),
      // 欠損予定ステータスコードリスト
      lossyoteiStsCodelst: this.fb.nonNullable.control({ value: this.mode == this.MODE_APPROVE ? ["04", "05",] : ["01", "02",], disabled: false }),
      // 承認画面フラグ
      apprvGamenFlg: this.fb.nonNullable.control({ value: this.mode == this.MODE_APPROVE ? "1" : "0", disabled: false }),
    });

    this.parentForm.addControl("cndt", this.searchConditionForm);

  }

  /**
   * SGKE903Aレスポンスからセレクターにオプションをセット
   * @private
   * @param {string} value
   * @param {string} viewValue
   * @return {SelectBox[]}
   * @memberof Sgke400dConditionComponent
   */
  private getSelector(value: string, viewValue: string, companyAreaInfoList: Array<Sgke903aResponseCompanyAreaInfo>): SelectBox[] {

    // SGKE903Aレスポンスのコードとコード値をまとめてリスト化
    let retSelectBoxList: SelectBox[] = [];
    companyAreaInfoList.map(data => {
      const select: SelectBox = { value: data[value], viewValue: data[viewValue] };
      retSelectBoxList.push(select);
    });

    // 重複を削除しソート
    const uniqueSortedArray = Array.from(
      new Map(retSelectBoxList.map(item => [item.value, item])).values()
    ).sort((a, b) => a.value > b.value ? 1 : -1);

    return uniqueSortedArray;
  }

  /**
   * 料金区分リスト設定処理
   * @private
   * @returns {void}
   * @memberof Sgke400dConditionComponent
   */
  private setChargeDivList(): void {
    this.chargeDivList = this.cndtAuthorityList.map(aItem => {
      const match = this.codeConversionMstMap.get('S300620').find(bItem => bItem.userCode === aItem.chargeDivcd);
      const select: SelectBox = { value: match.userCode, viewValue: match.hyojiCont };
      return select;
    });
  }

}
